import React from 'react';

import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';

import { getPartTitle } from '../utils';

const ConsumptionModal = ({ fabricWiseMeasurements, onClose, cadConsumptions }) => {
	if (!fabricWiseMeasurements || Object.keys(fabricWiseMeasurements).length === 0) {
		return (
			<LoaderComponent loading={false}>
				<ModalContainer className="width-1200-modal">
					<div className="common-popup conumption-modal w-100 body-scroll">
						<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
							<div className="popup-tilte">
								<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
								<small className="regular-14 gray_dark_01">
									According to the measurement chart, fabric consumption has been calculated
									below:
								</small>
							</div>
							<div className="close-btn bg-white" onClick={onClose}>
								<CommonImage alt="close" src="/icons/close.svg" />
							</div>
						</div>
						<div className="common-popup-body scroll-y-label">
							<p className="text-center p-4">No consumption data available</p>
						</div>
					</div>
				</ModalContainer>
			</LoaderComponent>
		);
	}

	const renderCadConsumption = () => {
		if (cadConsumptions && cadConsumptions?.length > 0) {
			return (
				<div className={`consumption-table scroll-x-label mb-3`}>
					<h3 className="regular-14 mb-2">CAD Consumptions</h3>
					<table className="table height-sm with-border mb-0">
						<thead>
							<tr>
								<th>Shell No</th>
								<th>Length (inch)</th>
								<th>L. Allow (inch)</th>
								<th>Width (inch)</th>
								<th>W. Allow (inch)</th>
								<th>GSM</th>
								<th>Wastage (%)</th>
								<th>Consumption (Kg)</th>
							</tr>
						</thead>
						<tbody>
							{cadConsumptions?.map((cad, index) => {
								return (
									<tr key={`consumption_row_${index}`}>
										<td className="body-part-column">
											<p>{getPartTitle(`part${index + 1}`)}</p>
										</td>
										<td className="length-column">
											<p>{cad?.fabric_length_inch}</p>
										</td>
										<td className="length-with-allowance-column">
											<p>10%</p>
										</td>
										<td className="width-column">
											<p>{cad?.fabric_width_inch}</p>
										</td>
										<td className="width-with-allowance-column">
											<p>2</p>
										</td>
										<td className="gsm-column">
											<p>{cad?.gsm}</p>
										</td>
										<td className="wastage-column">
											<p>
												{(1 - parseFloat(cad?.cutting_waste_percentage))?.toFixed(2) *
													100}
												%
											</p>
										</td>
										<td className={'consumption-column'}>
											<p>{cad?.consumption}</p>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					<div className="total-consumption table-footer p-2">
						<div className="d-flex justify-content-start">
							<p className="regular-14 gray_dark_01 weight-500">Total Consumption</p>
							<p className="regular-14 gray_dark_01 ml-2 weight-500">
								{cadConsumptions?.reduce(
									(total, cad) => total + parseFloat(cad?.consumption || 0),
									0
								)}{' '}
								Kg
							</p>
						</div>
					</div>
				</div>
			);
		}
	};

	return (
		<LoaderComponent loading={false}>
			<ModalContainer className="width-1200-modal">
				<div className="common-popup conumption-modal w-100 body-scroll">
					<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
						<div className="popup-tilte w-75">
							<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
							<small className="regular-14 gray_dark_01">
								According to the measurement chart, fabric consumption has been calculated
								below:
							</small>
						</div>
						<div className="close-btn bg-white" onClick={onClose}>
							<CommonImage alt="close" src="/icons/close.svg" />
						</div>
					</div>
					<div className="common-popup-body scroll-y-label">
						{renderCadConsumption()}
						{Object.entries(fabricWiseMeasurements).map(([partKey, parts], index) => {
							if (!Array.isArray(parts) || parts.length === 0) return null;

							const totalConsumption = parts.reduce((sum, part) => {
								const consumption = part.final_fabric_consumption_kg;
								const value =
									typeof consumption === 'string' ? parseFloat(consumption) : consumption;
								return sum + (isNaN(value) ? 0 : value);
							}, 0);

							const partTitle = getPartTitle(`part${index + 1}`);
							const isFirstTable = index === 0;

							return (
								<div
									key={partKey}
									className={`consumption-table scroll-x-label ${!isFirstTable ? 'mt-3' : ''}`}
								>
									<h3 className="regular-14 mb-2">Measurement Chart - {partTitle}</h3>
									<table className="table height-sm with-border mb-0">
										<thead>
											<tr>
												<th>Body Part</th>
												<th>Length (cm)</th>
												<th>L. Allow (cm)</th>
												<th>Width (cm)</th>
												<th>W. Allow (cm)</th>
												<th>GSM</th>
												<th>Wastage (%)</th>
												<th>Consumption (Kg)</th>
											</tr>
										</thead>
										<tbody>
											{parts.map((part, partIndex) => {
												const isLastRow = partIndex === parts.length - 1;

												return (
													<tr key={partIndex}>
														<td className="body-part-column">
															<p>{part.part || '-'}</p>
														</td>
														<td className="length-column">
															<p>{part.length_cm}</p>
														</td>
														<td className="length-with-allowance-column">
															<p>
																{part.final_length_with_allowance_cm -
																	part.length_cm}
															</p>
														</td>
														<td className="width-column">
															<p>{part.width_cm}</p>
														</td>
														<td className="width-with-allowance-column">
															<p>
																{part.final_width_with_allowance_cm -
																	part.width_cm}
															</p>
														</td>
														<td className="gsm-column">
															<p>{part.gsm}</p>
														</td>
														<td className="wastage-column">
															<p>{part.wastage_percentage}</p>
														</td>
														<td className={isLastRow ? 'consumption-column' : ''}>
															<p>
																{part.final_fabric_consumption_kg.toFixed(2)}
															</p>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
									<div className="total-consumption table-footer p-2">
										<div className="d-flex justify-content-start">
											<p className="regular-14 gray_dark_01 weight-500">
												Total Consumption
											</p>
											<p className="regular-14 gray_dark_01 ml-2 weight-500">
												{totalConsumption.toFixed(2)} Kg
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ConsumptionModal;
